/**
 * ZIP Export Utility
 * Generates a deployable ZIP archive of the site
 */

import JSZip from 'jszip'
import { optimizeImage, getOptimizedFilename } from './imageOptimizer'

/**
 * Generate and download the site as a ZIP file
 * @param {Object} options
 * @param {Object} options.files - HTML files { filename: content }
 * @param {Array} options.images - Generated images [{ url, filename }]
 * @param {string} options.domain - Site domain
 * @returns {Promise<Blob>} The generated ZIP blob
 */
export async function generateZip({ files, images, domain }) {
  const zip = new JSZip()
  const imgFolder = zip.folder('img')

  // Download, optimize and add images to img/ folder
  const imageRenames = {}
  if (images && images.length > 0) {
    console.log('📦 [EXPORT] Optimizing', images.length, 'images...')
    let totalSavings = 0

    for (const img of images) {
      if (img.url && img.filename) {
        try {
          console.log('📦 [EXPORT] Optimizing:', img.filename)
          const result = await optimizeImage(img.url, img.filename)
          const newFilename = getOptimizedFilename(img.filename, result.format)

          imgFolder.file(newFilename, result.blob)

          if (newFilename !== img.filename) {
            imageRenames[img.filename] = newFilename
          }

          totalSavings += result.originalSize - result.optimizedSize
          console.log(`📦 [EXPORT] Added: ${newFilename} (${Math.round(result.blob.size/1024)}KB, -${result.savings}%)`)
        } catch (err) {
          console.warn('📦 [EXPORT] Optimization failed, using original:', img.filename, err.message)
          try {
            const response = await fetch(img.url)
            if (response.ok) {
              const blob = await response.blob()
              imgFolder.file(img.filename, blob)
            }
          } catch {
            console.error('📦 [EXPORT] Could not fetch:', img.filename)
          }
        }
      }
    }
    console.log(`📦 [EXPORT] Total image savings: ${Math.round(totalSavings/1024)}KB`)
  }

  // Update HTML files with new image filenames
  let updatedFiles = { ...files }
  if (Object.keys(imageRenames).length > 0) {
    console.log('📦 [EXPORT] Updating HTML with new image filenames:', imageRenames)
    for (const [filename, content] of Object.entries(updatedFiles)) {
      let updated = content
      for (const [oldName, newName] of Object.entries(imageRenames)) {
        updated = updated.replace(new RegExp(oldName.replace('.', '\\.'), 'g'), newName)
      }
      updatedFiles[filename] = updated
    }
  }

  // Convert internal .html links to clean URLs
  console.log('📦 [EXPORT] Converting links to clean URLs...')
  for (const [filename, content] of Object.entries(updatedFiles)) {
    let updated = content
    updated = updated.replace(/href="index\.html"/g, 'href="/"')
    updated = updated.replace(/href="\.\/index\.html"/g, 'href="/"')
    updated = updated.replace(/href="([^"]+)\.html"/g, 'href="/$1"')
    updated = updated.replace(/href="\/\//g, 'href="/')
    updatedFiles[filename] = updated
  }

  // Add all HTML files
  Object.entries(updatedFiles).forEach(([filename, content]) => {
    zip.file(filename, content)
  })

  // Add robots.txt
  zip.file('robots.txt', `User-agent: *\nAllow: /\nSitemap: https://${domain}/sitemap.xml`)

  // Add sitemap.xml
  const pages = Object.keys(files).filter(f => f.endsWith('.html'))
  const sitemapXml = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${pages.map(page => {
  const cleanUrl = page === 'index.html' ? '' : page.replace('.html', '/')
  return `  <url><loc>https://${domain}/${cleanUrl}</loc></url>`
}).join('\n')}
</urlset>`
  zip.file('sitemap.xml', sitemapXml)

  // Add .htaccess
  const htaccess = `# EMD Factory - Configuration Apache
# ===================================
RewriteEngine On

# 1. Redirect .html to clean URL (SEO + user-friendly)
RewriteCond %{THE_REQUEST} /([^.]+)\\.html [NC]
RewriteRule ^ /%1 [R=301,L]

# 2. Serve .html file for clean URLs (internal rewrite)
RewriteCond %{REQUEST_FILENAME} !-f
RewriteCond %{REQUEST_FILENAME} !-d
RewriteCond %{REQUEST_FILENAME}.html -f
RewriteRule ^(.*)$ $1.html [L]

DirectoryIndex index.html

<IfModule mod_headers.c>
  Header set X-Content-Type-Options "nosniff"
  Header set X-Frame-Options "SAMEORIGIN"
</IfModule>

<IfModule mod_expires.c>
  ExpiresActive On
  ExpiresByType image/webp "access plus 1 month"
  ExpiresByType image/jpeg "access plus 1 month"
  ExpiresByType image/png "access plus 1 month"
</IfModule>

Options -Indexes
ErrorDocument 404 /404.html
`
  zip.file('.htaccess', htaccess)

  // Add 404.html if not present
  if (!files['404.html']) {
    const page404 = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Page non trouvée - ${domain}</title>
  <script src="https://cdn.tailwindcss.com"></script>
</head>
<body class="bg-gray-100 min-h-screen flex items-center justify-center">
  <div class="text-center p-8">
    <h1 class="text-6xl font-bold text-gray-300 mb-4">404</h1>
    <p class="text-xl text-gray-600 mb-6">Page non trouvée</p>
    <a href="/" class="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
      Retour à l'accueil
    </a>
  </div>
</body>
</html>`
    zip.file('404.html', page404)
  }

  return await zip.generateAsync({ type: 'blob' })
}

/**
 * Download a ZIP blob as a file
 */
export function downloadZip(blob, filename) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * Generate and download ZIP in one call
 */
export async function exportAndDownload({ files, images, domain }) {
  const blob = await generateZip({ files, images, domain })
  downloadZip(blob, `${domain || 'emd-site'}.zip`)
  return blob
}
